import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Award, Target, TrendingUp } from 'lucide-react';
import { Badge, Button, EmptyState, LoadingState, SectionHeading } from '../components/ui';
import { loadAllMyResults, type ResultRow } from '../lib/studentUtilities';

export function StudentPerformance() {
  const [results, setResults] = useState<ResultRow[]>();
  useEffect(() => { let active = true; void loadAllMyResults().then((rows) => { if (active) setResults(rows); }); return () => { active = false; }; }, []);

  const stats = useMemo(() => {
    const scored = (results ?? []).filter((row) => Number.isFinite(Number(row.Percentage)));
    const average = scored.length ? Math.round(scored.reduce((sum, row) => sum + Number(row.Percentage), 0) / scored.length) : 0;
    const best = scored.reduce((peak, row) => Math.max(peak, Number(row.Percentage)), 0);
    const recent = [...scored].slice(0, 10).reverse();
    return { scored, average, best, recent };
  }, [results]);

  if (!results) return <LoadingState />;
  if (!stats.scored.length) return <div className="utility-page">
    <section className="utility-hero"><div className="utility-icon"><Activity size={28} /></div><div><h1>Performance Analysis</h1><p>Your score trends across exam attempts.</p></div></section>
    <EmptyState icon={Target} title="No data yet" description="Submit an exam-mode test and your performance trend will build here." action={<Link to="/tests"><Button variant="soft">Take a test</Button></Link>} />
  </div>;

  return <div className="utility-page">
    <section className="utility-hero"><div className="utility-icon"><Activity size={28} /></div><div><h1>Performance Analysis</h1><p>{stats.scored.length} scored attempts analysed.</p></div></section>
    <section className="test-stats">
      <div><TrendingUp size={18} /><strong>{stats.average}%</strong><span>Average score</span></div>
      <div><Award size={18} /><strong>{stats.best}%</strong><span>Best</span></div>
      <div><Target size={18} /><strong>{stats.scored.length}</strong><span>Attempts</span></div>
    </section>
    <section className="detail-section">
      <SectionHeading title="Recent score trend" />
      <div className="trend-chart">{stats.recent.map((row) => <div className="trend-column" key={row.objectId}>
        <strong>{Number(row.Percentage)}%</strong>
        <div className="trend-bar"><i style={{ '--trend-scale': Math.max(4, Math.min(100, Number(row.Percentage))) / 100 } as CSSProperties} /></div>
        <small>{row.TestDate ? new Date(row.TestDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : ''}</small>
      </div>)}</div>
    </section>
    <section className="detail-section">
      <SectionHeading title="Grade distribution" />
      <div className="grade-grid">{['A', 'B', 'C', 'D'].map((grade) => {
        const count = stats.scored.filter((row) => String(row.YourGrade ?? '').toUpperCase().startsWith(grade)).length;
        return <div key={grade} className="grade-cell"><strong>{count}</strong><span>Grade {grade}</span></div>;
      })}</div>
    </section>
    <section className="detail-section">
      <SectionHeading title="All attempts" />
      <div className="subject-list">{[...stats.scored].slice(0, 20).map((row) => <Link className="subject-row" key={row.objectId} to={`/tests/result/${row.objectId}`}>
        <span><Activity size={17} /></span>
        <div><strong>{row.TestTitle ?? 'Test'}</strong><small>{row.TestDate ? new Date(row.TestDate).toLocaleDateString() : ''}</small></div>
        <Badge tone={Number(row.Percentage ?? 0) >= 60 ? 'green' : Number(row.Percentage ?? 0) >= 40 ? 'teal' : 'red'}>{Number(row.Percentage ?? 0)}%</Badge>
      </Link>)}</div>
    </section>
  </div>;
}
